import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import UserProfileMenu from '../components/UserProfileMenu';
import { useAuth } from '../hooks/useAuth';
import { useBlogPosts, articleParagraphs, type BlogPost } from '../data/blog';
import { BRAND } from '../brand';
import { ExternalLink, BookOpen } from 'lucide-react';

/* Articles — the signed-in reading view of the same store the public /blog
   serves (Firestore `blog/current`, brand-filtered and published-only via
   useBlogPosts). Routes: /articles and /articles/:slug.

   Deliberately one store, not two: publishing once in /admin/blog puts the
   article on the public site AND in here. Kept visually in step with Blog.tsx
   so a client sees the same article treatment either side of login. */

const IS_CORP = BRAND === 'corporate';
const ACCENT = IS_CORP ? '#E8890B' : '#1976d2';
const ACCENT_D = IS_CORP ? '#C76B12' : '#0C447C';
const ACCENT_BG = IS_CORP ? '#FFF4E5' : '#EFF6FF';
const INK = '#111827', INK2 = '#42505F', MUTED = '#6B7280', LINE = '#E9EDF2';

const fmtDate = (iso: string) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? '' : new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Deterministic wash so an image-less article still has a face. */
const wash = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return `linear-gradient(135deg, hsl(${h} 42% 93%), hsl(${(h + 42) % 360} 48% 86%))`;
};

function Pill({ children, tone = 'accent' }: { children: React.ReactNode; tone?: 'accent' | 'plain' }) {
  return (
    <span style={{
      background: tone === 'accent' ? ACCENT_BG : '#F1F5F9',
      color: tone === 'accent' ? ACCENT_D : MUTED,
      fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase',
      padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function Byline({ p, size = 12 }: { p: BlogPost; size?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: size, color: MUTED, fontWeight: 600 }}>
      <span style={{ width: 22, height: 22, borderRadius: '50%', background: ACCENT, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 800, flex: 'none' }}>
        {(p.author || 'C').trim().charAt(0).toUpperCase()}
      </span>
      <span>{p.author}</span>
      {p.publishedAt && <><span style={{ color: '#CBD5E1' }}>·</span><span>{fmtDate(p.publishedAt)}</span></>}
      {p.readTime && <><span style={{ color: '#CBD5E1' }}>·</span><span>{p.readTime}</span></>}
    </div>
  );
}

function Card({ post, onOpen }: { post: BlogPost; onOpen: () => void }) {
  return (
    <button onClick={onOpen} className="text-left bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all"
      style={{ borderColor: LINE, display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: 150, background: post.image ? undefined : wash(post.slug || post.title), position: 'relative' }}>
        {post.image
          ? <img src={post.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: ACCENT, opacity: .45 }}><BookOpen className="w-8 h-8" /></div>}
      </div>
      <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
        <div><Pill>{post.category || 'Insight'}</Pill></div>
        <h3 style={{ fontSize: 15.5, fontWeight: 800, color: INK, lineHeight: 1.35 }}>{post.title}</h3>
        {post.excerpt && (
          <p style={{ fontSize: 13, color: INK2, lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {post.excerpt}
          </p>
        )}
        <div style={{ marginTop: 'auto', paddingTop: 8 }}><Byline p={post} size={11.5} /></div>
      </div>
    </button>
  );
}

export default function Articles() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { posts, loading } = useBlogPosts();
  const [cat, setCat] = useState('All');

  const post = slug ? posts.find(p => p.slug === slug) : null;
  const others = post ? posts.filter(p => p.slug !== post.slug).slice(0, 3) : [];

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(posts.map(p => p.category).filter(Boolean)))],
    [posts],
  );
  const visible = cat === 'All' ? posts : posts.filter(p => p.category === cat);
  const [featured, ...rest] = visible;

  return (
    <div className="min-h-screen bg-[#EBEBEF] text-slate-900">
      <Sidebar onLogout={logout} />
      <main className="md:ms-64 flex-1 overflow-y-auto bg-[#EBEBEF] relative min-h-screen">
        <MobileNav onLogout={logout} />
        <div className="absolute top-6 end-8 z-10"><UserProfileMenu /></div>

        <div className="mx-auto px-6 py-12 mt-8" style={{ maxWidth: 1100 }}>
          <div className="mb-2 flex items-center gap-1 text-sm" style={{ color: MUTED }}>
            <button onClick={() => navigate('/dashboard')} className="hover:text-gray-900">My Applications</button>
            <span>/</span>
            {slug
              ? <button onClick={() => navigate('/articles')} className="hover:text-gray-900">Articles</button>
              : <span style={{ color: INK, fontWeight: 500 }}>Articles</span>}
            {slug && <><span>/</span><span style={{ color: INK, fontWeight: 500 }}>Article</span></>}
          </div>

          {loading ? (
            <div className="py-24 flex justify-center">
              <div className="w-10 h-10 border-4 rounded-full animate-spin" style={{ borderColor: ACCENT, borderTopColor: 'transparent' }} />
            </div>
          ) : slug ? (
            /* ── Single article ─────────────────────────────────────── */
            !post ? (
              <div className="bg-white rounded-2xl border p-10 text-center" style={{ borderColor: LINE }}>
                <p style={{ color: INK2, fontWeight: 600 }}>That article isn’t available.</p>
                <Link to="/articles" style={{ color: ACCENT, fontWeight: 800, fontSize: 14, display: 'inline-block', marginTop: 12 }}>← All articles</Link>
              </div>
            ) : (
              <>
                <article className="bg-white rounded-2xl border shadow-sm overflow-hidden" style={{ borderColor: LINE }}>
                  {post.image
                    ? <img src={post.image} alt="" style={{ width: '100%', maxHeight: 380, objectFit: 'cover', display: 'block' }} />
                    : <div style={{ height: 120, background: wash(post.slug || post.title) }} />}

                  <div style={{ padding: '30px clamp(20px,4vw,46px) 38px' }}>
                    <div style={{ marginBottom: 14 }}><Pill>{post.category || 'Insight'}</Pill></div>
                    <h1 style={{ fontSize: 'clamp(24px,3.2vw,34px)', fontWeight: 900, color: INK, lineHeight: 1.2, letterSpacing: '-.025em', marginBottom: 14 }}>
                      {post.title}
                    </h1>
                    <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: `1px solid ${LINE}` }}>
                      <Byline p={post} size={12.5} />
                    </div>

                    <div style={{ maxWidth: 760, margin: '0 auto' }}>
                      {articleParagraphs(post).map((para, i) => (
                        <p key={i} style={{ fontSize: 16, color: INK2, lineHeight: 1.85, marginBottom: 17, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{para}</p>
                      ))}
                    </div>

                    {/* Drafted-from-a-headline articles credit and link the publisher. */}
                    {post.sourceUrl && (
                      <a href={post.sourceUrl} target="_blank" rel="noopener noreferrer nofollow"
                        className="inline-flex items-center gap-2 mt-2 px-4 py-2.5 rounded-xl font-bold text-sm hover:opacity-90"
                        style={{ background: ACCENT_BG, color: ACCENT_D }}>
                        Read the original{post.sourceName ? ` at ${post.sourceName}` : ''} <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </article>

                {others.length > 0 && (
                  <>
                    <h2 style={{ fontSize: 18, fontWeight: 800, color: INK, margin: '32px 0 14px' }}>More articles</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {others.map(p => <Card key={p.id} post={p} onOpen={() => navigate(`/articles/${p.slug}`)} />)}
                    </div>
                  </>
                )}
              </>
            )
          ) : (
            /* ── List ───────────────────────────────────────────────── */
            <>
              <div className="mb-5">
                <h1 className="text-[36px] font-bold leading-tight" style={{ color: INK }}>Articles</h1>
                <p className="text-sm mt-1" style={{ color: MUTED }}>
                  {posts.length} article{posts.length !== 1 ? 's' : ''} from the Conncct team
                  {IS_CORP ? ' — tax, compliance and company setup' : ' — readiness, structuring and capital'}
                </p>
              </div>

              {categories.length > 2 && (
                <div className="flex gap-2 flex-wrap mb-6">
                  {categories.map(c => (
                    <button key={c} onClick={() => setCat(c)} style={{
                      border: `1.5px solid ${cat === c ? ACCENT : LINE}`,
                      background: cat === c ? ACCENT : '#fff',
                      color: cat === c ? '#fff' : INK2,
                      fontWeight: 700, fontSize: 12.5, padding: '6px 13px', borderRadius: 999,
                    }}>{c}</button>
                  ))}
                </div>
              )}

              {visible.length === 0 ? (
                <div className="bg-white rounded-2xl border p-12 text-center" style={{ borderColor: LINE }}>
                  <BookOpen className="w-8 h-8 mx-auto mb-3" style={{ color: ACCENT, opacity: .6 }} />
                  <p style={{ color: INK2, fontWeight: 600 }}>No articles published yet.</p>
                  <p style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>New pieces from the team will show up here.</p>
                </div>
              ) : (
                <>
                  {/* Featured */}
                  {featured && (
                    <button onClick={() => navigate(`/articles/${featured.slug}`)}
                      className="w-full text-left bg-white rounded-2xl border shadow-sm overflow-hidden hover:shadow-md transition-all mb-6 art-feat"
                      style={{ borderColor: LINE, display: 'grid', gridTemplateColumns: '1.05fr .95fr' }}>
                      <div style={{ minHeight: 230, background: featured.image ? undefined : wash(featured.slug || featured.title) }}>
                        {featured.image && <img src={featured.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
                      </div>
                      <div style={{ padding: '26px 28px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 11 }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Pill>{featured.category || 'Insight'}</Pill>
                          <Pill tone="plain">Latest</Pill>
                        </div>
                        <h2 style={{ fontSize: 24, fontWeight: 900, color: INK, lineHeight: 1.25, letterSpacing: '-.02em' }}>{featured.title}</h2>
                        {featured.excerpt && <p style={{ fontSize: 14, color: INK2, lineHeight: 1.7 }}>{featured.excerpt}</p>}
                        <Byline p={featured} />
                        <span style={{ color: ACCENT_D, fontWeight: 800, fontSize: 13.5 }}>Read the article →</span>
                      </div>
                    </button>
                  )}

                  {rest.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {rest.map(p => <Card key={p.id} post={p} onOpen={() => navigate(`/articles/${p.slug}`)} />)}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <style>{`@media (max-width: 860px){ .art-feat{ grid-template-columns: minmax(0,1fr) !important; } }`}</style>
      </main>
    </div>
  );
}
